/**
 * Generic type for the persistence utility
 */
type StorageValue<T> = T | null;

/**
 * Storage keys used in the application
 */
export const STORAGE_KEYS = {
  AUTH: "auth",
  USER: "user",
  SESSION: "session",
} as const;

/**
 * Persistence utility for managing localStorage
 */
export const persistence = {
  /**
   * Set a value in localStorage
   * @param key - The key to store the value under
   * @param value - The value to store
   */
  set<T>(key: string, value: T): void {
    try {
      const serializedValue = JSON.stringify(value);
      localStorage.setItem(key, serializedValue);
    } catch (error) {
      console.error("Error saving to localStorage:", error);
    }
  },

  /**
   * Get a value from localStorage
   * @param key - The key to retrieve the value from
   * @returns The stored value or null if not found
   */
  get<T>(key: string): StorageValue<T> {
    try {
      const serializedValue = localStorage.getItem(key);
      return serializedValue ? JSON.parse(serializedValue) : null;
    } catch (error) {
      console.error("Error reading from localStorage:", error);
      return null;
    }
  },

  /**
   * Remove a value from localStorage
   * @param key - The key to remove
   */
  remove(key: string): void {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.error("Error removing from localStorage:", error);
    }
  },

  /**
   * Clear all values from localStorage
   */
  clear(): void {
    try {
      localStorage.clear();
    } catch (error) {
      console.error("Error clearing localStorage:", error);
    }
  },

  /**
   * Check if a key exists in localStorage
   * @param key - The key to check
   * @returns True if the key exists, false otherwise
   */
  has(key: string): boolean {
    try {
      return localStorage.getItem(key) !== null;
    } catch (error) {
      console.error("Error checking localStorage:", error);
      return false;
    }
  },
};
